import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { DocumentDuplicateIcon, CheckIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const STATUS_FILTERS = [
  { id: 'unused', label: 'Available' },
  { id: 'mine', label: 'My claims' }
];

const STATUS_BADGE = {
  unused: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  in_use: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  used: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
};

const VATargetUsernames = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('unused');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectingHundred, setSelectingHundred] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/va/target-usernames', {
        params: { status, search: search || undefined, page, limit: 50 }
      });
      if (res.data.success) {
        setItems(res.data.items || []);
        setPagination(res.data.pagination || { total: 0, pages: 1 });
      }
    } catch (err) {
      toast.error('Failed to load targets');
    } finally {
      setLoading(false);
    }
  }, [status, search, page]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [status, search, page]);

  const claimableOnPage = items.filter((item) => item.status === 'unused');
  const claimablePageIds = claimableOnPage.map((item) => String(item._id));
  const allPageSelected = claimablePageIds.length > 0 && claimablePageIds.every((id) => selectedIds.has(id));
  const somePageSelected = claimablePageIds.some((id) => selectedIds.has(id));

  const toggleSelect = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        claimablePageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        claimablePageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const selectHundred = async () => {
    setSelectingHundred(true);
    try {
      const res = await api.get('/va/target-usernames', {
        params: {
          status: 'unused',
          search: search || undefined,
          page: 1,
          limit: 100
        }
      });
      if (res.data.success) {
        const ids = (res.data.items || [])
          .filter((item) => item.status === 'unused')
          .map((item) => String(item._id));
        setSelectedIds(new Set(ids));
        if (ids.length === 0) {
          toast.error('No available usernames to select');
        } else {
          toast.success(`Selected ${ids.length} username${ids.length === 1 ? '' : 's'}`);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to select usernames');
    } finally {
      setSelectingHundred(false);
    }
  };

  const copyLink = (username) => {
    navigator.clipboard.writeText(`https://x.com/${username}`);
    toast.success('Profile link copied');
  };

  const claim = async (id) => {
    try {
      await api.patch(`/va/target-usernames/${id}/claim`);
      toast.success('Username claimed');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not claim');
    }
  };

  const bulkClaim = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.patch('/va/target-usernames/bulk-claim', {
        ids: Array.from(selectedIds)
      });
      const { claimed = 0, failed = 0 } = res.data;
      if (claimed > 0) {
        toast.success(`Claimed ${claimed} username${claimed === 1 ? '' : 's'}`);
      }
      if (failed > 0) {
        toast.error(`${failed} username${failed === 1 ? '' : 's'} could not be claimed`);
      }
      setSelectedIds(new Set());
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk claim failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const release = async (id) => {
    try {
      await api.patch(`/va/target-usernames/${id}/release`);
      toast.success('Released');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not release');
    }
  };

  const markUsed = async (id) => {
    try {
      await api.patch(`/va/target-usernames/${id}/mark-used`);
      toast.success('Marked as used');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update');
    }
  };

  const showBulkToolbar = status === 'unused';

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-indigo-200/80 dark:border-indigo-800/60 bg-gradient-to-br from-indigo-50/90 via-white to-violet-50/50 dark:from-indigo-950/40 dark:via-gray-800 dark:to-violet-950/20 p-5 shadow-sm">
        <div className="flex gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <InformationCircleIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="min-w-0 space-y-3 text-sm text-slate-700 dark:text-slate-300">
            <p>
              These are our <strong className="font-semibold text-slate-900 dark:text-white">target usernames</strong>.
              You will follow the <strong className="font-semibold text-slate-900 dark:text-white">followers</strong> of these specified accounts.
            </p>
            <p>
              Claim <strong className="font-semibold text-slate-900 dark:text-white">1 target username per account</strong> you manage.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl border border-emerald-200/80 dark:border-emerald-800/50 bg-emerald-50/60 dark:bg-emerald-950/20 px-3 py-2.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300 mb-1.5">Follow only</p>
                <ul className="space-y-1 text-slate-700 dark:text-slate-300">
                  <li>Male users</li>
                  <li>American, European &amp; English names</li>
                </ul>
              </div>
              <div className="rounded-xl border border-rose-200/80 dark:border-rose-800/50 bg-rose-50/60 dark:bg-rose-950/20 px-3 py-2.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-rose-800 dark:text-rose-300 mb-1.5">Do not follow</p>
                <ul className="space-y-1 text-slate-700 dark:text-slate-300">
                  <li>Arabic, Indian, Asian &amp; African users</li>
                  <li>Females</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => { setStatus(f.id); setPage(1); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              status === f.id
                ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-sm'
                : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            {f.label}
          </button>
        ))}
        <input
          type="search"
          placeholder="Search username..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="ml-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm min-w-[200px]"
        />
      </div>

      {showBulkToolbar && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-indigo-200/80 dark:border-indigo-800/50 bg-indigo-50/50 dark:bg-indigo-950/20 px-4 py-3">
          <span className="text-sm text-slate-600 dark:text-slate-400">
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select usernames to bulk claim'}
          </span>
          <button
            type="button"
            onClick={selectHundred}
            disabled={selectingHundred || bulkLoading}
            className="px-3 py-1.5 rounded-lg border border-indigo-200 dark:border-indigo-700 bg-white dark:bg-gray-800 text-sm font-medium text-indigo-700 dark:text-indigo-300 disabled:opacity-50"
          >
            {selectingHundred ? 'Selecting…' : 'Select 100'}
          </button>
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={bulkClaim}
              disabled={bulkLoading}
              className="btn-primary !py-1.5 !px-4 !text-sm disabled:opacity-50"
            >
              {bulkLoading ? 'Claiming…' : `Bulk claim (${selectedIds.size})`}
            </button>
          )}
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkLoading}
              className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Clear selection
            </button>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No usernames in this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  {showBulkToolbar && (
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = somePageSelected && !allPageSelected;
                        }}
                        onChange={toggleSelectPage}
                        disabled={claimablePageIds.length === 0}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        aria-label="Select all on page"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3">Username</th>
                  <th className="px-4 py-3">Status</th>
                  {status === 'mine' && <th className="px-4 py-3">In use by</th>}
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((item) => {
                  const isClaimable = item.status === 'unused';
                  const isSelected = selectedIds.has(String(item._id));
                  return (
                    <tr
                      key={item._id}
                      className={`hover:bg-slate-50/50 dark:hover:bg-slate-800/30 ${isSelected ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}
                    >
                      {showBulkToolbar && (
                        <td className="px-4 py-3">
                          {isClaimable ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(item._id)}
                              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                              aria-label={`Select @${item.username}`}
                            />
                          ) : null}
                        </td>
                      )}
                      <td className="px-4 py-3 font-mono text-slate-900 dark:text-white">@{item.username}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_BADGE[item.status] || ''}`}>
                          {item.status.replace('_', ' ')}
                        </span>
                      </td>
                      {status === 'mine' && (
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{item.inUseByVaName || '—'}</td>
                      )}
                      <td className="px-4 py-3 text-slate-500">{item.source?.replace('_', ' ')}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => copyLink(item.username)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            <DocumentDuplicateIcon className="w-4 h-4" /> Copy link
                          </button>
                          {item.status === 'unused' && (
                            <button type="button" onClick={() => claim(item._id)} className="btn-primary !py-1.5 !px-3 !text-xs">
                              Claim
                            </button>
                          )}
                          {item.status === 'in_use' && (
                            <>
                              <button type="button" onClick={() => release(item._id)} className="px-3 py-1.5 rounded-lg border text-xs">
                                Release
                              </button>
                              <button type="button" onClick={() => markUsed(item._id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs">
                                <CheckIcon className="w-4 h-4" /> Mark used
                              </button>
                            </>
                          )}
                          {item.status === 'unused' && (
                            <button type="button" onClick={() => markUsed(item._id)} className="px-3 py-1.5 rounded-lg text-xs text-emerald-700 border border-emerald-200">
                              Mark used
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pagination.pages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-slate-100 dark:border-slate-800">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 rounded border disabled:opacity-40">Prev</button>
            <span className="text-sm text-slate-500 py-1">Page {page} of {pagination.pages}</span>
            <button type="button" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 rounded border disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VATargetUsernames;
